<template>
  <div
    class="inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-xs not-prose dark:text-white"
    :style="customColor && customColorStyle"
    :class="[type ? chipClasses : '', { 'h-7': icon }]"
  >
    <em v-if="icon" :class="['mdi', icon, { 'mr-1 ': text }]" />
    <span v-if="number !== undefined" class="mr-1.5 pl-0.5 font-bold">{{
      number
    }}</span>

    <span class="truncate max-w-[10rem]">{{ text }}</span>
    <em
      v-if="iconEnd"
      :class="[
        'mdi text-xl',
        iconEnd,
        { 'ml-1 ': text },
        iconColor ? iconClasses : '',
      ]"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  text?: string;
  icon?: string;
  iconEnd?: string;
  iconColor?: string;
  number?: string | number;
  type?: string;
  customColor?: string;
  borderColor?: string;
  outlined?: boolean;
}>();

const customColorStyle = computed(() => {
  const styles: Record<string, string> = {};

  if (props.customColor) {
    if (props.outlined) {
      styles.border = `1px solid ${props.customColor}`;
      styles.color = props.customColor;
    } else {
      styles.background = props.customColor;
      styles.border = `1px solid ${props.customColor}`;
      styles.color = 'white';
    }
  }

  if (props.borderColor) {
    styles.border = `1px solid ${props.borderColor}`;
  }

  return styles;
});

const chipClasses = computed(() => {
  let cssString = 'border-2';
  switch (props.type) {
    case 'info':
      cssString = 'border border-zinc-500 dark:border-zinc-400';
      if (props.outlined) {
        cssString = `${cssString} text-zinc-500 dark:text-zinc-300`;
      } else {
        cssString = `${cssString} bg-zinc-500 dark:bg-zinc-300 text-zinc-50 dark:text-zinc-900`;
      }
      break;
    case 'danger':
      cssString = 'border border-light-error dark:border-dark-error';
      if (props.outlined) {
        cssString = `${cssString} radiocheck-status-error text-light-error`;
      } else {
        cssString = `${cssString} bg-light-error dark:bg-dark-error text-gray-50 dark:text-white`;
      }
      break;
    case 'error':
      cssString = 'border border-light-error dark:border-dark-error';
      if (props.outlined) {
        cssString = `${cssString} radiocheck-status-error text-light-error`;
      } else {
        cssString = `${cssString} bg-light-error dark:bg-dark-error text-gray-50 dark:text-white`;
      }
      break;
    case 'success':
      cssString = 'border border-light-success dark:border-dark-success';
      if (props.outlined) {
        cssString = `${cssString} text-light-success dark:text-dark-success`;
      } else {
        cssString = `${cssString} bg-light-success dark:bg-dark-success text-gray-50`;
      }
      break;
    case 'warning':
      cssString = 'border border-light-warning dark:border-dark-warning';
      if (props.outlined) {
        cssString = `${cssString} text-light-warning dark:text-dark-warning`;
      } else {
        cssString = `${cssString} bg-light-warning dark:bg-dark-warning text-gray-50 font-bold`;
      }
      break;
    case 'successInfo':
      cssString =
        'border border-light-success bg-light-0 text-light-success dark:border-dark-success dark:bg-dark-0 dark:text-dark-success';
      break;
    case 'activeInfo':
      cssString =
        'border border-zinc-500 dark:border-zinc-400 bg-primary-0 dark:bg-primary-0 text-white';
      break;
    default:
      break;
  }
  return cssString;
});

const iconClasses = computed(() => {
  let cssString = '';
  switch (props.iconColor) {
    case 'info':
      cssString = 'text-zinc-500 dark:text-zinc-400';
      break;
    case 'error':
      cssString = 'text-light-error dark:text-dark-error';
      break;
    case 'success':
      cssString = 'text-light-success dark:text-dark-success';
      break;
    case 'warning':
      cssString = 'text-light-warning dark:text-dark-warning';
      break;
    default:
      break;
  }
  return cssString;
});
</script>
